// The API client.
//
// Session lives in an HttpOnly cookie, so nothing here touches a token. A 401
// on any call but sign-in means the session died: the app is told once, and
// every screen reacts to that rather than each handling it separately.

/**
 * Where the API lives.
 *
 * Empty means same origin: in production the server serves this bundle itself,
 * and in development Vite proxies /api to it. VITE_API_URL points the app at an
 * API on another origin instead — http://localhost:5000 against a web app on
 * :5173, say. That is cross-origin, so the request must carry credentials
 * explicitly and the server must name this origin in COCOMA_WEB_ORIGIN. The
 * session cookie itself still travels: differing ports make a different origin
 * but the same *site*, which is what SameSite=Strict governs.
 */
const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');
const url = (path: string) => BASE + path;

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

/**
 * What to say when the response carries no message of its own. A bare status
 * number tells the person nothing, and an unreachable API is the likeliest
 * failure on a fresh deployment — so it gets named rather than numbered.
 */
function describe(status: number): string {
  if (status === 401) return 'That username and password did not match.';
  if (status === 403) return 'You do not have access to that.';
  if (status === 404) return 'That was not found.';
  if (status === 409) return 'That conflicts with something already recorded.';
  if (status === 429) return 'Too many attempts. Wait a few minutes and try again.';
  if (status >= 500) return 'The server could not complete that. If it keeps happening, check the server log.';
  return `The request failed (${status}).`;
}

type Listener = () => void;
const signedOut = new Set<Listener>();
export const onSignedOut = (fn: Listener) => { signedOut.add(fn); return () => signedOut.delete(fn); };

async function request<T>(method: string, path: string, body?: unknown, raw?: { blob: Blob }): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url(path), {
      method,
      credentials: BASE ? 'include' : 'same-origin',
      headers: raw ? { 'content-type': raw.blob.type || 'application/octet-stream' }
                   : (body ? { 'content-type': 'application/json' } : undefined),
      body: raw ? raw.blob : (body ? JSON.stringify(body) : undefined),
    });
  } catch {
    // fetch only rejects when the request never reached a server at all.
    throw new ApiError(0, 'Cannot reach the server. Check that it is running.');
  }

  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    signedOut.forEach((fn) => fn());
    throw new ApiError(401, 'Your session has ended. Please sign in again.');
  }

  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { error: text }; }
  if (!res.ok) {
    const said = (data as { error?: string })?.error?.trim();
    throw new ApiError(res.status, said || describe(res.status));
  }
  return data as T;
}

const qs = (q?: Record<string, string | number | null | undefined>) => {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v != null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const api = {
  get: <T>(path: string, q?: Record<string, string | number | null | undefined>) =>
    request<T>('GET', path + qs(q)),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload: <T>(path: string, blob: Blob) => request<T>('POST', path, undefined, { blob }),
  /** A download the browser handles itself — Tally exports, mainly. */
  download: (path: string, q?: Record<string, string | number>) => {
    const a = document.createElement('a');
    a.href = url(path + qs(q));
    a.download = '';
    a.click();
  },
};
